import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from '@dukanti/shared';
import type { Courier, CourierErrorKey, TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const e164Regex = /^\+[1-9]\d{7,14}$/;

const courierFormSchema = z.object({
  name: z.string().trim().min(1, 'admin.couriers.errors.nameRequired'),
  phoneNumber: z.string().trim().regex(e164Regex, 'admin.couriers.errors.phoneInvalid'),
  active: z.boolean(),
  internalNotes: z.string().trim().max(500, 'admin.couriers.errors.notesTooLong').optional(),
});

type CourierFormValues = z.infer<typeof courierFormSchema>;

export interface CourierFormSubmit {
  readonly name: string;
  readonly phoneNumber: string;
  readonly active: boolean;
  readonly internalNotes: string | null;
}

export interface CourierFormDialogProps {
  readonly open: boolean;
  readonly courier: Courier | null;
  readonly isSubmitting?: boolean;
  readonly errorKey?: CourierErrorKey | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (values: CourierFormSubmit) => Promise<void>;
}

export function CourierFormDialog({
  open,
  courier,
  isSubmitting = false,
  errorKey,
  onOpenChange,
  onSubmit,
}: CourierFormDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const isEdit = !!courier;
  const defaultValues = useMemo<CourierFormValues>(
    () => ({
      name: courier?.name ?? '',
      phoneNumber: courier?.phone_number ?? '',
      active: courier?.active ?? true,
      internalNotes: courier?.internal_notes ?? '',
    }),
    [courier],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CourierFormValues>({
    resolver: zodResolver(courierFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [defaultValues, open, reset]);

  const submit = handleSubmit(async (values) => {
    await onSubmit({
      name: values.name.trim(),
      phoneNumber: values.phoneNumber.trim(),
      active: values.active,
      internalNotes: values.internalNotes?.trim() ? values.internalNotes.trim() : null,
    });
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !isSubmitting && onOpenChange(next)}>
      <DialogContent dir={dir} data-testid="courier-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {t(
              (isEdit
                ? 'admin.couriers.form.title.edit'
                : 'admin.couriers.form.title.create') as TranslationKey,
            )}
          </DialogTitle>
          <DialogDescription>
            {t('admin.couriers.form.description' as TranslationKey)}
          </DialogDescription>
        </DialogHeader>

        <form noValidate onSubmit={submit} className="space-y-5">
          {errorKey ? (
            <div
              role="alert"
              className="border-destructive/25 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-4 py-3 text-start text-sm"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{t(errorKey as TranslationKey)}</span>
            </div>
          ) : null}

          <div>
            <Label htmlFor="courier-name" className="mb-1.5 block text-start">
              {t('admin.couriers.form.nameLabel' as TranslationKey)}
            </Label>
            <Input
              id="courier-name"
              type="text"
              autoComplete="name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'courier-name-error' : undefined}
              data-testid="courier-name-input"
              {...register('name')}
            />
            {errors.name ? (
              <p id="courier-name-error" role="alert" className="text-destructive mt-1 text-xs">
                {t(errors.name.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="courier-phone" className="mb-1.5 block text-start">
              {t('admin.couriers.form.phoneLabel' as TranslationKey)}
            </Label>
            <Input
              id="courier-phone"
              type="tel"
              dir="ltr"
              autoComplete="tel"
              aria-invalid={!!errors.phoneNumber}
              aria-describedby={errors.phoneNumber ? 'courier-phone-error' : undefined}
              data-testid="courier-phone-input"
              {...register('phoneNumber')}
            />
            {errors.phoneNumber ? (
              <p id="courier-phone-error" role="alert" className="text-destructive mt-1 text-xs">
                {t(errors.phoneNumber.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="courier-notes" className="mb-1.5 block text-start">
              {t('admin.couriers.form.notesLabel' as TranslationKey)}
            </Label>
            <Textarea
              id="courier-notes"
              rows={4}
              aria-invalid={!!errors.internalNotes}
              aria-describedby={errors.internalNotes ? 'courier-notes-error' : undefined}
              data-testid="courier-notes-input"
              {...register('internalNotes')}
            />
            {errors.internalNotes ? (
              <p id="courier-notes-error" role="alert" className="text-destructive mt-1 text-xs">
                {t(errors.internalNotes.message as TranslationKey)}
              </p>
            ) : null}
          </div>

          <label className="flex items-center gap-3 text-start text-sm">
            <input
              type="checkbox"
              className="border-input text-admin-lime focus:ring-admin-lime size-4 rounded"
              {...register('active')}
            />
            <span>{t('admin.couriers.form.activeLabel' as TranslationKey)}</span>
          </label>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting || undefined}>
              {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span>{t('admin.couriers.form.save' as TranslationKey)}</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
